import { debounce, isEmpty, sortBy } from "lodash";
import { useRouter } from "next/router";
import { Sidebar } from "primereact/sidebar";
import { TreeSelect } from "primereact/treeselect";
import { classNames } from "primereact/utils";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCompanies } from "../../../../../../common/api/useCompanies";
import type { TreeNode } from "../../../../../../common/components/LocationTreeSelect";
import { LocationTreeSelect } from "../../../../../../common/components/LocationTreeSelect";
import { useRedirect } from "../../../../../../common/hooks/useRedirect";
import { useMissingIntegration } from "../../../../../integration/utils/hooks";
import { IconBetterspace } from "../../../../../layout/components/Icons";
import { ZENDESK_URLS } from "../../../../constants";
import { INSTALLATION_ROUTES } from "../../../../constants/router.constants";
import {
    DevicesOverviewIcon,
    KnowledgeBaseIcon,
    LogsIcon,
    MapMarkerIcon,
    OverviewIcon,
    SpacesOverviewIcon,
} from "../Icons";
import LocationTreeSelectValueTemplate from "./templates/LocationTreeSelectValueTemplate";

interface HeaderProps {}

const Header = (props: HeaderProps) => {
    const {} = props;

    const { t } = useTranslation("installation");

    const router = useRouter();
    const { companyId, locationId } = router.query as {
        companyId?: string;
        locationId?: string;
    };

    const { triggerZendeskRedirect } = useRedirect();

    const { showMissingIntegrationModal } = useMissingIntegration();

    const treeSelectRef = useRef<TreeSelect | null>(null);

    const [search, setSearch] = useState(""),
        [showTopBar, setShowTopBar] = useState<boolean>(false),
        [options, setOptions] = useState<TreeNode[] | undefined>(undefined);

    const { companies } = useCompanies(),
        currentLocation = companies
            ?.find((company) => company.id === companyId)
            ?.locations?.find((location) => location.id === locationId);

    const preprocessedOptions: TreeNode[] = useMemo(() => {
        return sortBy(companies, (company) => company.name?.toLowerCase())
            .map((company) => ({
                ...company,
                locations: sortBy(company.locations ?? [], (location) =>
                    location.name?.toLowerCase()
                ),
            }))
            .map((company) => ({
                id: company.id,
                name: company.name,
                isExpanded: company.id === companyId,
                children: company.locations.map((location) => ({
                    id: location.id,
                    name: location.name,
                    isActive: location.id === locationId,
                })),
            }));
    }, [companies, companyId, locationId]);

    useEffect(() => {
        if (options === undefined && preprocessedOptions.length > 0) {
            setOptions(preprocessedOptions);
        }
    }, [preprocessedOptions, options]);

    const handleNavigation = ({
        pathname,
        query,
    }: {
        pathname?: string;
        query?: Record<string, string>;
    }) => {
        const newPathname = pathname ?? router.pathname;
        const newQuery = query ?? router.query;

        router.push({ pathname: newPathname, query: newQuery }).then(() => {
            setShowTopBar(false);
            treeSelectRef.current?.hide();
        });
    };

    const handleLocationSelect = ({
        companyId,
        locationId,
    }: {
        companyId: string;
        locationId: string;
    }) => {
        showMissingIntegrationModal({
            companyId,
            locationId,
        });

        handleNavigation({
            pathname: INSTALLATION_ROUTES.SPACES, // For some reasons I don't understand, the user should always route to spaces after the location changes
            query: {
                companyId,
                locationId,
            },
        });
    };

    const handleHideDropdown = () => {
        setSearch("");
        setOptions(preprocessedOptions);
    };

    const handleSearch = (value: string) => {
        setSearch(value);
        handleFilterOptions(value);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleFilterOptions = useCallback(
        debounce((searchValue: string) => {
            if (searchValue === "") {
                setOptions(preprocessedOptions);
                return;
            }

            const lowerSearch = searchValue.toLowerCase();

            const newOptions = preprocessedOptions
                .map((company) => {
                    if (company.name.toLowerCase().includes(lowerSearch)) {
                        return company;
                    }

                    const locations = company.children?.filter((location) =>
                        location.name.toLowerCase().includes(lowerSearch)
                    );

                    return !isEmpty(locations)
                        ? { ...company, isExpanded: true, children: locations }
                        : null;
                })
                .filter(Boolean) as TreeNode[];

            setOptions(newOptions);
        }, 500),
        [preprocessedOptions]
    );

    return (
        <div className="px-4 pt-4 flex justify-between items-start">
            <Sidebar
                visible={showTopBar}
                position="top"
                onHide={() => setShowTopBar(false)}
                header={() => (
                    <LocationTreeSelect
                        onHide={handleHideDropdown}
                        onLocationSelect={handleLocationSelect}
                        onSearchChange={handleSearch}
                        onToggleExpand={setOptions}
                        options={options}
                        ref={treeSelectRef}
                        search={search}
                        selectedLocation={currentLocation}
                        valueTemplate={
                            <LocationTreeSelectValueTemplate
                                location={currentLocation}
                            />
                        }
                    />
                )}
                className="rounded-b-md overflow-hidden h-fit"
                pt={{
                    header: {
                        className:
                            "flex flex-row-reverse px-4 py-0 pt-4 items-start",
                    },
                    content: {
                        className: "flex flex-col justify-between",
                    },
                    closeButton: {
                        className: "size-fit p-4 -ml-4 -mt-4 text-default",
                    },
                    closeIcon: {
                        className: "size-6",
                    },
                    icons: {
                        className: "size-fit",
                    },
                }}
            >
                <div className="flex flex-col gap-4 w-fit">
                    <div
                        className={classNames(
                            "flex gap-4 cursor-pointer hover:text-primary-default transition-colors duration-150 py-2",
                            {
                                "text-primary-default":
                                    router.pathname ===
                                    INSTALLATION_ROUTES.SUMMARY,
                            }
                        )}
                        onClick={() => {
                            handleNavigation({
                                pathname: INSTALLATION_ROUTES.SUMMARY,
                            });
                        }}
                    >
                        <OverviewIcon />
                        <span className="text-[1.5rem] font-bold">
                            {t("summary.self")}
                        </span>
                    </div>
                    <div
                        className={classNames(
                            "flex gap-4 cursor-pointer hover:text-primary-default transition-colors duration-150 py-2",
                            {
                                "text-primary-default":
                                    router.pathname ===
                                    INSTALLATION_ROUTES.SPACES,
                            }
                        )}
                        onClick={() => {
                            handleNavigation({
                                pathname: INSTALLATION_ROUTES.SPACES,
                            });
                        }}
                    >
                        <SpacesOverviewIcon />
                        <span className="text-[1.5rem] font-bold">
                            {t("spaces.self")}
                        </span>
                    </div>
                    <div
                        className={classNames(
                            "flex gap-4 cursor-pointer hover:text-primary-default transition-colors duration-150 py-2",
                            {
                                "text-primary-default":
                                    router.pathname ===
                                    INSTALLATION_ROUTES.DEVICES,
                            }
                        )}
                        onClick={() => {
                            handleNavigation({
                                pathname: INSTALLATION_ROUTES.DEVICES,
                            });
                        }}
                    >
                        <DevicesOverviewIcon />

                        <span className="text-[1.5rem] font-bold">
                            {t("devices.self")}
                        </span>
                    </div>
                    <div
                        className={classNames(
                            "flex gap-4 cursor-pointer hover:text-primary-default transition-colors duration-150 py-2",
                            {
                                "text-primary-default":
                                    router.pathname ===
                                    INSTALLATION_ROUTES.LOGS,
                            }
                        )}
                        onClick={() => {
                            handleNavigation({
                                pathname: INSTALLATION_ROUTES.LOGS,
                            });
                        }}
                    >
                        <LogsIcon />
                        <span className="text-[1.5rem] font-bold">
                            {t("logs.self")}
                        </span>
                    </div>
                </div>
                <div className="flex flex-col gap-4 w-fit mt-20">
                    <div
                        className="flex gap-4 cursor-pointer hover:text-primary-default transition-colors duration-150 py-2 w-fit"
                        onClick={() => {
                            window.open(`/`, "_blank"); // Go to default app page
                        }}
                    >
                        <div className="size-8">
                            <IconBetterspace />
                        </div>

                        <span className="text-[1.5rem] font-bold">
                            better.energy
                        </span>
                    </div>
                    <div
                        className="flex gap-4 cursor-pointer hover:text-primary-default transition-colors duration-150 py-2 w-fit"
                        onClick={() => {
                            triggerZendeskRedirect(ZENDESK_URLS.HELPCENTER);
                        }}
                    >
                        <KnowledgeBaseIcon />
                        <span className="text-[1.5rem] font-bold">
                            {t("knowledgeBase")}
                        </span>
                    </div>
                </div>
            </Sidebar>
            <button
                className="p-4 -ml-4 -mt-4 flex"
                onClick={() => {
                    setShowTopBar(true);
                }}
            >
                <i className="pi pi-bars text-[1.5rem]" />
            </button>
            <div className="text-default/80 font-bold flex gap-2 items-center min-w-0">
                <MapMarkerIcon />
                <span className="truncate block whitespace-nowrap overflow-hidden text-ellipsis">
                    {currentLocation?.name}
                </span>
            </div>
        </div>
    );
};

export default Header;
